export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

/** YYYY-MM-DD in the browser's local timezone, suitable for <input type="date">. */
export function toLocalDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** YYYY-MM-DD for an ISO instant as seen in the given IANA timezone (not the browser's). */
export function dateInTimeZone(iso: string, timeZone: string): string {
  // en-CA formats as YYYY-MM-DD, which is exactly the <input type="date"> value shape.
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(iso),
  );
}

/** 0-23 hour-of-day for an ISO instant as seen in the given IANA timezone
 * (not the browser's) — used to bucket exact slots into Morning/Afternoon/
 * Evening groups (see booking-form.ts's renderSlots). */
export function hourInTimeZone(iso: string, timeZone: string): number {
  const hour = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hour12: false }).format(new Date(iso));
  // hour12: false can format midnight as "24" instead of "0" depending on the
  // runtime's ICU data — normalize so callers always get a plain 0-23 value.
  return Number(hour) % 24;
}

/** "9:00 AM" for an ISO instant as seen in the given IANA timezone — no date,
 * no zone abbreviation. Used for slot/window picker buttons and group
 * headers, where the calendar date is already implied by the selected date
 * field above and the doctor's timezone is implicit to the clinic itself;
 * spelling either out again on every button is both redundant and, at typical
 * button widths, exactly what forces the slot grid to wrap onto two lines per
 * button and bloat the form's height. */
export function timeInTimeZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true }).format(
    new Date(iso),
  );
}

/** Strips the zone token (e.g. "IST", "GMT+5:30" — which one depends on the
 * runtime's ICU data, see availability/service.ts's `zzz` formatting) that a
 * backend-composed label sticks right after the clock time, e.g. "9:00 AM
 * IST" -> "9:00 AM". Scoped to this one display case (the post-booking
 * confirmation screens) rather than changing the backend field itself, which
 * WhatsApp/email confirmations and the AI chat still consume unchanged and
 * benefit from keeping the zone in an async, textual channel.
 *
 * Anchored at the END of the string, not "whatever follows the first AM/PM" —
 * a window-range label has two AM/PM occurrences, and matching the first ate
 * the " – " separator instead of the trailing zone (root-caused live,
 * 2026-09-27, in the AI chat's equivalent of this same helper — see
 * apps/api/src/modules/chat/tool-handlers.ts's identical fix). */
export function stripTimeZoneAbbrev(label: string): string {
  return label.replace(/\s+(?:GMT[+-]\d{1,2}(?::\d{2})?|(?!AM\b|PM\b)[A-Za-z]{2,6})\s*$/i, '');
}
